// Темп хода строительства обычного («неявного») сетевого бота: живые игроки
// планируют стройку в чужой ход (см. construction/planning) — их план
// ставится мгновенно в начале хода, а с чекбоксом «Автоматически завершить
// ход» ход и завершается сразу. Бот, который всегда строит с паузами, выделялся
// бы на их фоне. Поэтому в начале своего хода строительства бот сразу решает,
// «планировал» ли он:
//  - PACED — нет: строит с обычными паузами, как раньше;
//  - PLANNED — да, но без автозавершения: все строения мгновенно, а ход
//    завершает через обычную паузу;
//  - PLANNED_AUTO_COMPLETE — да, с автозавершением: все строения мгновенно и
//    сразу завершение хода.
// Если бот решил купить политику, «планировал» отменяется и ход идет обычным
// темпом: политику план не покупает, живой игрок покупает ее сам, кликом (см.
// botWorkStepper).
//
// Бросок делает сервер один раз на ход (работу бота) и присылает делегату
// вместе с работой: переделегированный посреди хода бот продолжает в том же
// темпе. Модуль общий для сервера и клиента.

import {GAME_TYPES} from '../../../constants/game';
import {PHASES} from '../../../constants/phases';
import {IS_CONSTRUCTION_PLAN_AUTO_COMPLETE_TURN_BUTTON_SHOWN} from '../../../configs/constructionPlanAutoCompleteTurn';

export const BOT_CONSTRUCTION_TURN_PACINGS = {
  PACED: 'paced',
  PLANNED: 'planned',
  PLANNED_AUTO_COMPLETE: 'planned_auto_complete',
};

// Доли темпов: 30% — как раньше, 30% — план без автозавершения, 40% — план с
// автозавершением
export const BOT_CONSTRUCTION_TURN_PACING_CHANCES = {
  [BOT_CONSTRUCTION_TURN_PACINGS.PACED]: 0.3,
  [BOT_CONSTRUCTION_TURN_PACINGS.PLANNED]: 0.3,
  [BOT_CONSTRUCTION_TURN_PACINGS.PLANNED_AUTO_COMPLETE]: 0.4,
};

/** Бросок темпа на один ход бота: число из [0, 1) */
export const rollBotConstructionTurnPacing = () => Math.random();

/**
 * Мог ли бот в этот ход строительства притвориться планировавшим — ровно тогда,
 * когда планировать мог бы живой игрок на его месте:
 *  - сетевая партия, фаза строительства, ходит этот бот;
 *  - бот не «явный»: явный помечен ботом для всех, притворяться ему незачем;
 *  - до него в этой фазе уже строил кто-то другой: планируют в чужой ход, а
 *    первому в фазе планировать было некогда.
 */
export const checkIfBotCouldPlanConstructionTurn = (state, botPlayerId) => {
  const gameState = state?.shared?.gameState;
  if (!gameState || gameState.gameType !== GAME_TYPES.ONLINE || gameState.phase !== PHASES.CONSTRUCTION) {
    return false;
  }
  const currentPlayer = (gameState.players || [])[gameState.currentPlayerIndex];
  if (!currentPlayer || currentPlayer.id !== botPlayerId || !currentPlayer.isBot || currentPlayer.isVisibleBot) {
    return false;
  }
  return (gameState.phaseCompletedPlayers || []).some((playerId) => playerId !== botPlayerId);
};

/**
 * Темп хода строительства бота по броску сервера (pacingRoll из работы бота).
 * Без броска или когда планировать было нельзя — PACED.
 *
 * isAutoCompleteAvailable — есть ли у живых игроков кнопка «Автоматически
 * завершить ход» (по умолчанию — флаг в configs/constructionPlanAutoCompleteTurn).
 * Пока ее нет, бот с автозавершением выдавал бы себя: его доля бросков уходит
 * в PLANNED.
 */
export const resolveBotConstructionTurnPacing = (
  state,
  botPlayerId,
  pacingRoll,
  isAutoCompleteAvailable = IS_CONSTRUCTION_PLAN_AUTO_COMPLETE_TURN_BUTTON_SHOWN
) => {
  if (typeof pacingRoll !== 'number' || !checkIfBotCouldPlanConstructionTurn(state, botPlayerId)) {
    return BOT_CONSTRUCTION_TURN_PACINGS.PACED;
  }
  const pacedChance = BOT_CONSTRUCTION_TURN_PACING_CHANCES[BOT_CONSTRUCTION_TURN_PACINGS.PACED];
  const plannedChance = BOT_CONSTRUCTION_TURN_PACING_CHANCES[BOT_CONSTRUCTION_TURN_PACINGS.PLANNED];
  if (pacingRoll < pacedChance) {
    return BOT_CONSTRUCTION_TURN_PACINGS.PACED;
  }
  if (pacingRoll < pacedChance + plannedChance || !isAutoCompleteAvailable) {
    return BOT_CONSTRUCTION_TURN_PACINGS.PLANNED;
  }
  return BOT_CONSTRUCTION_TURN_PACINGS.PLANNED_AUTO_COMPLETE;
};
